import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios'; // Certifique-se de ter axios instalado
import { toast } from 'sonner'; // Ou sua lib de toast preferida

// Caminhos das imagens base
const TRUCK_BASE_IMAGE = "/img/truck_only_transparent_white.webp";
const TRAILER_BASE_IMAGE = "/img/trailer_only_transparent_white.webp";

// Lista de tipos de veículos disponíveis para simulação
const VEHICLE_TYPES = [
    { slug: 'sedan', label: 'Sedan' },
    { slug: 'suv', label: 'SUV' },
    { slug: 'van', label: 'Van' },
    { slug: 'pickup', label: 'Pickup' },
];

/**
 * Componente TintedVehicle (Mantido conforme seu snippet)
 */
const TintedVehicle = ({ src, isOperable, style, alt = 'vehicle' }) => {
  const [maskSupported, setMaskSupported] = useState(false);
  const [status, setStatus] = useState('loading'); 

  useEffect(() => {
    try {
      const ok =
        typeof CSS !== 'undefined' &&
        typeof CSS.supports === 'function' &&
        (CSS.supports('mask-image', 'url("")') ||
          CSS.supports('-webkit-mask-image', 'url("")'));
      setMaskSupported(!!ok);
    } catch (e) {
      setMaskSupported(false);
    }
  }, []);

  const color = isOperable
    ? 'rgba(19, 218, 99, 0.95)' // emerald-500
    : 'rgba(244, 63, 94, 0.95)'; // rose-500

  const preloadImg = (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none', left: -9999, top: -9999 }}
      onLoad={() => setStatus('ready')}
      onError={() => setStatus('error')}
    />
  );

  if (status === 'error') return preloadImg;

  return (
    <>
      {preloadImg}
      {status === 'ready' && (
        maskSupported ? (
          <div
            className="absolute z-20 pointer-events-none transition-all duration-300"
            style={{
              ...style,
              backgroundColor: color,
              WebkitMaskImage: `url("${src}")`,
              WebkitMaskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              WebkitMaskSize: 'contain',
              maskImage: `url("${src}")`,
              maskRepeat: 'no-repeat',
              maskPosition: 'center',
              maskSize: 'contain',
              filter: 'drop-shadow(0 0 0.18rem rgba(0,0,0,0.55))',
            }}
          />
        ) : (
          <img
            src={src}
            alt={alt}
            className="absolute z-20 pointer-events-none transition-all duration-300"
            style={{
              ...style,
              opacity: 0.95,
              filter: isOperable
                ? 'grayscale(1) sepia(1) hue-rotate(90deg) saturate(600%) brightness(1.1)'
                : 'grayscale(1) sepia(1) hue-rotate(320deg) saturate(600%) brightness(0.9)',
            }}
          />
        )
      )}
    </>
  );
};

const TrailerDiagram = ({ planData }) => {
  // Modos: 'view', 'calibration', 'simulation'
  const [mode, setMode] = useState('view'); 
  
  // Dados locais
  const [localSlots, setLocalSlots] = useState([]);
  
  // --- Estados de Simulação ---
  // simulationVehicles: cópia editável dos veículos para enviar ao backend
  const [simulationVehicles, setSimulationVehicles] = useState([]);
  // simulatedPlan: o resultado retornado pelo endpoint de teste
  const [simulatedPlan, setSimulatedPlan] = useState(null);
  const [isSimulatingLoading, setIsSimulatingLoading] = useState(false);

  const svgRef = useRef(null);
  const [interaction, setInteraction] = useState({ type: null, id: null });

  // Inicializa Slots e Veículos quando o planData muda (Load inicial)
  useEffect(() => {
    if (planData) {
      // Setup para Calibração
      const slots = planData
        .filter(item => item.slot)
        .map(item => ({ 
           ...item.slot, 
           vehicle: item.vehicle,
           width: item.slot.width || 17, 
           height: item.slot.height || 7 
        }));
      setLocalSlots(slots);

      // Setup para Simulação (Extraindo lista de veículos)
      // Juntamos os alocados + overflow para a lista de edição
      const allVehicles = [
          ...planData.filter(i => i.slot).map(i => i.vehicle),
          ...planData.filter(i => !i.slot).map(i => i.vehicle) // overflow items
      ].filter(Boolean); // remove nulls
      
      setSimulationVehicles(allVehicles);
    }
  }, [planData]);

  // --- LÓGICA DE SIMULAÇÃO (API CALL) ---
  
  // Função que chama o endpoint de teste
  const runSimulation = useCallback(async (vehiclesToSimulate) => {
      setIsSimulatingLoading(true);
      try {
          // Precisamos do token de auth aqui, pegando do localStorage conforme seu padrão
          const token = localStorage.getItem('token') || localStorage.getItem('access_token');
          
          const response = await axios.post('https://new.cajuu.app/api/load-planning/simulate', {
              vehicles: vehiclesToSimulate
          }, {
              headers: { 
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                  'Screen-Name':'/Load'
              }
          });

          if (response.data && response.data.data) {
              setSimulatedPlan(response.data.data);
          }
      } catch (error) {
          console.error("Simulation failed", error);
          toast.error("Failed to update simulation.");
      } finally {
          setIsSimulatingLoading(false);
      }
  }, []);

  // Debounce: Chama a simulação quando o usuário para de mexer por 500ms
  useEffect(() => {
      if (mode !== 'simulation') return;
      
      const timeoutId = setTimeout(() => {
          runSimulation(simulationVehicles);
      }, 500);

      return () => clearTimeout(timeoutId);
  }, [simulationVehicles, mode, runSimulation]);


  // Manipuladores de Mudança nos Inputs de Simulação
  const handleVehicleChange = (vehicleId, field, value) => {
      setSimulationVehicles(prev => prev.map(v => {
          if (v.id === vehicleId) {
              return { ...v, [field]: value };
          }
          return v;
      }));
  };

  // --- HELPER DE IMAGENS ---
  const getVehicleImage = (typeSlug, positionSlug) => {
    return `/img/vehicles/${typeSlug}_${positionSlug}.webp`;
  };

  // --- LÓGICA DE CALIBRAÇÃO (Mantida igual) ---
  const getSvgCoords = (clientX, clientY) => {
      if (!svgRef.current) return { x: 0, y: 0 };
      const svgRect = svgRef.current.getBoundingClientRect();
      const x = ((clientX - svgRect.left) / svgRect.width) * 100;
      const y = ((clientY - svgRect.top) / svgRect.height) * 34.5;
      return { x, y };
  };

  const handleMouseDown = (e, slotId, type) => {
    if (mode !== 'calibration') return;
    e.stopPropagation(); e.preventDefault();
    setInteraction({ type, id: slotId });
  };

  const handleMouseMove = (e) => {
    if (mode !== 'calibration' || !interaction.type || !svgRef.current) return;
    const { x: mouseX, y: mouseY } = getSvgCoords(e.clientX, e.clientY);

    setLocalSlots(prev => prev.map(slot => {
        if (slot.id !== interaction.id) return slot;
        if (interaction.type === 'drag') {
            const newX = mouseX - (slot.width / 2);
            const newY = mouseY - (slot.height / 2);
            return { ...slot, x: parseFloat(newX.toFixed(2)), y: parseFloat(newY.toFixed(2)) };
        } else if (interaction.type === 'resize') {
            let newWidth = mouseX - slot.x;
            let newHeight = mouseY - slot.y;
            if (newWidth < 2) newWidth = 2; if (newHeight < 2) newHeight = 2;
            return { ...slot, width: parseFloat(newWidth.toFixed(2)), height: parseFloat(newHeight.toFixed(2)) };
        }
        return slot;
    }));
  };

  const handleMouseUp = () => setInteraction({ type: null, id: null });

  // --- SELEÇÃO DOS DADOS A EXIBIR ---
  
  // Decide qual fonte de dados usar baseado no modo atual
  let displayData = [];
  
  if (mode === 'calibration') {
      displayData = localSlots.map(slot => ({ slot, vehicle: slot.vehicle }));
  } else if (mode === 'simulation') {
      // Se tivermos um plano simulado, usamos ele. Se não (loading inicial), usamos o real.
      displayData = simulatedPlan || planData;
  } else {
      // Modo View Normal
      displayData = planData;
  }

  // Filtros finais para renderização
  const allocatedItems = displayData.filter(item => item.slot).map(item => ({
      ...item,
      // Garante width/height mesmo se vier sem do backend simulado
      slot: { ...item.slot, width: item.slot.width || 17, height: item.slot.height || 7 }
  }));
  
  // No modo simulação, o overflow é calculado com base no resultado da simulação
  const overflowItems = displayData.filter(item => !item.slot);

  return (
    <div 
        className={`p-4 rounded-xl shadow-2xl mt-6 border select-none transition-colors ${mode === 'simulation' ? 'border-indigo-500/50' : ' border-gray-500'}`}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
    >
      {/* Header e Controles de Modo */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
          <h3 className="text-white text-lg font-bold flex items-center gap-2">
            🚛 Load Guidance
            {mode === 'simulation' && <span className="text-xs bg-indigo-600 px-2 py-0.5 rounded text-white animate-pulse">SIMULATION MODE</span>}
          </h3>
          
          <div className="flex gap-2 bg-gray-950 p-1 rounded-lg border border-gray-800">
            <button 
                onClick={() => setMode('view')}
                className={`text-xs px-3 py-1.5 rounded-md font-bold transition-all ${mode === 'view' ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-300'}`}
            >
                View
            </button>
            <button 
                onClick={() => setMode('simulation')}
                className={`text-xs px-3 py-1.5 rounded-md font-bold transition-all ${mode === 'simulation' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20' : 'text-gray-500 hover:text-gray-300'}`}
            >
                Edit / Simulate
            </button>
            <button 
                onClick={() => setMode('calibration')}
                className={`text-xs px-3 py-1.5 rounded-md font-bold transition-all ${mode === 'calibration' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-300'}`}
            >
                Calibrate
            </button>
          </div>
      </div>
      
      {/* Container Gráfico (Diagrama) */}
      <div className="w-full relative rounded-lg overflow-hidden border border-gray-700/50 aspect-[2048/707]">
        
        {/* Loading Overlay na Simulação */}
        {mode === 'simulation' && isSimulatingLoading && (
            <div className="absolute inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mb-2"></div>
                    <span className="text-indigo-400 text-xs font-bold">Recalculating Load Plan...</span>
                </div>
            </div>
        )}

        {/* Bases */}
        <img src={TRUCK_BASE_IMAGE} className="absolute inset-0 w-full h-full object-contain pointer-events-none z-0 opacity-35" alt="" />
        <img src={TRAILER_BASE_IMAGE} className="absolute inset-0 w-full h-full object-contain pointer-events-none z-10 opacity-25" alt="" />

        {/* Veículos */}
        {allocatedItems.map((item) => {
            if (!item.vehicle) return null;
            const { x, y, width, height } = item.slot;
            
            // Posição CSS
            const style = {
                left: `${x}%`,
                top: `${(y / 34.5) * 100}%`,
                width: `${width}%`,
                height: `${(height / 34.5) * 100}%`
            };
            
            return (
                <TintedVehicle 
                    key={`v-${item.vehicle.id}`}
                    src={getVehicleImage(item.vehicle.type_slug, item.slot.position_slug)}
                    isOperable={!!item.vehicle.is_operable}
                    style={style}
                />
            );
        })}

        {/* Interatividade (SVG) */}
        <svg ref={svgRef} viewBox="0 0 100 34.5" className={`absolute inset-0 w-full h-full z-30 ${mode === 'calibration' ? 'cursor-default' : ''}`}>
          {allocatedItems.map((item) => {
             const { x, y, width, height, id } = item.slot;
             const isSelected = interaction.id === id;

             const calibrationStyle = mode === 'calibration' ? {
                 stroke: isSelected ? '#3b82f6' : 'rgba(255, 255, 255, 0.3)',
                 strokeWidth: 0.3,
                 strokeDasharray: '1,1',
                 fill: 'rgba(59, 130, 246, 0.1)',
                 cursor: 'move'
             } : {
                 stroke: 'transparent', strokeWidth: 0, fill: 'transparent', cursor: 'help'
             };

             return (
               <g key={id} className="group">
                 <rect 
                    x={x} y={y} width={width} height={height} rx="0.5"
                    {...calibrationStyle}
                    className={mode !== 'calibration' ? "transition-all duration-200 stroke-0 hover:stroke-white hover:stroke-1" : ""}
                    onMouseDown={(e) => handleMouseDown(e, id, 'drag')}
                 />
                 
                 {/* Alça Resize (Calibração) */}
                 {mode === 'calibration' && (
                     <circle 
                        cx={x + width} cy={y + height} r="1.2" 
                        fill="#3b82f6" stroke="white" strokeWidth="0.2" cursor="nwse-resize"
                        onMouseDown={(e) => handleMouseDown(e, id, 'resize')}
                     />
                 )}
                 {/* Labels (Calibração) */}
                 {mode === 'calibration' && (
                     <text x={x} y={y - 0.5} fontSize="1" fill="#3b82f6" fontWeight="bold">{width.toFixed(1)}x{height.toFixed(1)}</text>
                 )}
               </g>
             );
          })}
        </svg>
      </div>

      {/* --- PAINEL INFERIOR: Manifesto ou Editores --- */}
      <div className={`mt-4 p-3 rounded-lg border ${mode === 'simulation' ? 'bg-indigo-900/10 border-indigo-500/30' : 'bg-gray-800/50 border-gray-700'}`}>
         
         <div className="flex justify-between items-center mb-2">
             <p className={`text-xs font-bold uppercase tracking-wider ${mode === 'simulation' ? 'text-indigo-400' : 'text-gray-400'}`}>
                {mode === 'simulation' ? 'Simulate Vehicle Changes' : `Manifest (${allocatedItems.length})`}
             </p>
             {overflowItems.length > 0 && <span className="text-red-400 text-xs font-bold animate-pulse">⚠️ {overflowItems.length} Overflow (Not Loaded)</span>}
         </div>
         
         {mode === 'simulation' ? (
             // --- MODO SIMULAÇÃO: LISTA EDITÁVEL ---
             <div className="grid grid-cols-1 gap-2">
                 {simulationVehicles.map((vehicle) => (
                     <div key={vehicle.id} className="flex flex-col sm:flex-row justify-between items-center bg-gray-900 border border-gray-700 p-2 rounded gap-2">
                         
                         {/* Info Fixa */}
                         <div className="text-xs text-gray-400 w-full sm:w-auto truncate">
                             {vehicle.year} {vehicle.model}
                         </div>

                         {/* Controles */}
                         <div className="flex items-center gap-2 w-full sm:w-auto">
                             {/* Select de Tipo */}
                             <select 
                                value={vehicle.type_slug}
                                onChange={(e) => handleVehicleChange(vehicle.id, 'type_slug', e.target.value)}
                                className="bg-gray-800 text-white text-[10px] sm:text-xs border border-gray-600 rounded px-2 py-1 focus:border-indigo-500 outline-none"
                             >
                                 {VEHICLE_TYPES.map(t => <option key={t.slug} value={t.slug}>{t.label}</option>)}
                             </select>

                             {/* Toggle Operable */}
                             <button
                                onClick={() => handleVehicleChange(vehicle.id, 'is_operable', !vehicle.is_operable)}
                                className={`text-[10px] font-bold px-2 py-1 rounded border min-w-[3rem] transition-colors ${
                                    vehicle.is_operable 
                                    ? 'bg-emerald-900/30 text-emerald-400 border-emerald-800 hover:bg-emerald-900/50' 
                                    : 'bg-rose-900/30 text-rose-400 border-rose-800 hover:bg-rose-900/50'
                                }`}
                             >
                                {vehicle.is_operable ? 'OP' : 'INOP'}
                             </button>
                         </div>
                     </div>
                 ))}
                 <div className="text-[10px] text-gray-500 text-center mt-2">
                    * Changes are temporary and affect the visual plan only.
                 </div>
             </div>
         ) : mode === 'calibration' ? (
             // --- MODO CALIBRAÇÃO: OUTPUT PHP ---
              <div className="p-2 bg-black rounded border border-blue-500/50">
                  <h4 className="text-blue-400 font-bold text-xs mb-1">PHP Array Output</h4>
                  <textarea 
                    readOnly
                    className="w-full h-32 bg-gray-900 text-green-400 font-mono text-[10px] p-2 rounded border border-gray-700 outline-none"
                    value={generatePhpArray(localSlots)}
                    onClick={(e) => e.target.select()}
                  />
              </div>
         ) : (
             // --- MODO VIEW: MANIFESTO APENAS LEITURA ---
             <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {allocatedItems.filter(i=>i.vehicle).map(item => (
                    <div key={item.vehicle.id} className="flex justify-between items-center bg-gray-900 border border-gray-700 px-2 py-1.5 rounded text-xs hover:bg-gray-800 transition-colors">
                        <div className="flex items-center gap-2 overflow-hidden">
                            <span className="text-gray-400 font-bold bg-gray-800 px-1.5 py-0.5 rounded">Slot {item.slot.id}</span>
                            <span className="text-gray-200 truncate font-medium">{item.vehicle.year} {item.vehicle.model}</span>
                        </div>
                        <div className="shrink-0 ml-2">
                            {item.vehicle.is_operable ? 
                                <span className="text-emerald-400 text-[10px] bg-emerald-900/30 px-1.5 py-0.5 rounded border border-emerald-900/50">OP</span> : 
                                <span className="text-rose-400 text-[10px] bg-rose-900/30 px-1.5 py-0.5 rounded font-bold border border-rose-900/50">INOP</span>
                            }
                        </div>
                    </div>
                ))}
             </div>
         )}
      </div>
    </div>
  );
};

// Gerador PHP
const generatePhpArray = (slots) => {
    let output = "return [\n";
    const sorted = [...slots].sort((a,b) => a.id - b.id);
    sorted.forEach(slot => {
        output += `    [\n        'id' => ${slot.id},\n        'name' => '${slot.name}',\n        'deck' => '${slot.deck}',\n        'position_slug' => '${slot.position_slug}',\n        'x' => ${slot.x}, 'y' => ${slot.y},\n        'width' => ${slot.width}, 'height' => ${slot.height},\n        'max_weight' => ${slot.max_weight},\n        'is_front' => ${slot.is_front ? 'true' : 'false'}\n    ],\n`;
    });
    output += "];";
    return output;
};

export default TrailerDiagram;